import { useEffect, useMemo, useRef } from "react";

export function usePreviousChatMotion() {
  const stackRef = useRef<HTMLDivElement>(null);
  const motionRef = useRef({ offset: 0, velocity: 0, frame: 0 });

  // Gesture effects keep their bookkeeping while these callbacks retain their identity.
  const motion = useMemo(() => {
    function getOffset() {
      return motionRef.current.offset;
    }

    function applyOffset(offset: number) {
      motionRef.current.offset = offset;
      // Frame updates bypass React so dragging never re-renders either transcript.
      stackRef.current?.style.setProperty("--conversation-peek", `${offset}px`);
    }

    function setScrollOffset(offset: number) {
      stackRef.current?.style.setProperty(
        "--previous-chat-scroll-offset",
        `${offset}px`
      );
    }

    function stop() {
      const motion = motionRef.current;
      cancelAnimationFrame(motion.frame);
      motion.velocity = 0;
    }

    function jumpTo(offset: number) {
      stop();
      applyOffset(offset);
    }

    function settle(target: number, speed = 1) {
      const motion = motionRef.current;
      cancelAnimationFrame(motion.frame);
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        jumpTo(target);
        return;
      }
      let lastTime = performance.now();
      const step = (time: number) => {
        const elapsed = Math.max(0, Math.min((time - lastTime) / 1000, 0.032));
        lastTime = time;
        const steps = Math.ceil(speed);
        const dt = (elapsed * speed) / steps;
        let offset = motion.offset;
        for (let i = 0; i < steps; i++) {
          motion.velocity +=
            ((target - offset) * 360 - motion.velocity * 36) * dt;
          offset = Math.max(0, offset + motion.velocity * dt);
          if (offset === 0) motion.velocity = 0;
        }
        applyOffset(offset);
        if (
          Math.abs(motion.offset - target) < 0.25 &&
          Math.abs(motion.velocity) < 2
        ) {
          motion.velocity = 0;
          applyOffset(target);
          return;
        }
        motion.frame = requestAnimationFrame(step);
      };
      motion.frame = requestAnimationFrame(step);
    }

    return { getOffset, jumpTo, settle, stop, setScrollOffset };
  }, []);

  useEffect(() => {
    const motion = motionRef.current;
    return () => cancelAnimationFrame(motion.frame);
  }, []);

  return { stackRef, ...motion };
}
